import { compareDates, localDate, type LocalDate } from '../dates';
import { isValidLocalDate } from '../budget/schedule';
import type { DomainEvent } from '../events';
import type { Mutation } from '../mutations';
import type { BuildCtx } from '../spending/types';
import { tallyDay, type DayTally } from './daily';
import { costOf } from './price';
import { isTrigger } from './triggers';
import {
  quitMutation,
  type CravingLike,
  type QuitPlan,
  type QuitResult,
  type SmokeLogLike,
} from './types';

export const MAX_DAY_COUNT = 200;
export type LogError = 'bad_count' | 'bad_date' | 'future_date' | 'bad_trigger' | 'not_found';

export interface SmokeChange {
  mutation: Mutation;
  /** The day after the change. */
  tally: DayTally;
  event: DomainEvent;
}

export interface NewSmokeLog extends SmokeChange {
  log: SmokeLogLike;
}

type SmokeEventType = 'smoke.logged' | 'smoke.total_set' | 'smoke.removed';

function smokeEvent(
  type: SmokeEventType,
  smokeLogId: string,
  at: string,
  tally: DayTally,
  mutationId: string,
): DomainEvent {
  return {
    type,
    smokeLogId,
    at,
    date: tally.date,
    dayCount: tally.count,
    dayCostMinor: tally.costMinor,
    mutationId,
  };
}

function insertLog(log: SmokeLogLike, ctx: BuildCtx): Mutation {
  return quitMutation(ctx, 'smoke_logs', 'insert', log.id, {
    at: log.at,
    recordedTz: ctx.tz,
    date: log.date,
    kind: log.kind,
    count: log.count,
    costMinor: log.costMinor,
    currency: 'PHP',
    trigger: log.trigger,
  });
}

/** Cigarettes tapped on `date` after its latest written total (the taps that count). */
function tapsSinceTotal(dayLogs: readonly SmokeLogLike[], date: LocalDate): number {
  const live = dayLogs.filter((l) => l.deletedAt === null && l.date === date);
  const totals = live.filter((l) => l.kind === 'day_total');
  const t = tallyDay(live, date);
  if (!t.hasTotal) return t.count;
  // tallyDay picked the latest total; its count is the part of the day that isn't taps.
  const latest = totals.reduce((a, b) => (b.at > a.at || (b.at === a.at && b.id > a.id) ? b : a));
  return t.count - latest.count;
}

/**
 * `dayLogs` = every log already stored for today (any order; deleted ones are ignored). Each tap is
 * charged `costOf(n + 1) - costOf(n)`, n = taps since the latest written total, so a pack price
 * that doesn't divide evenly still adds up: 19 taps of a ₱159 / 19 pack cost ₱159.00, the same as
 * writing 19. (Taps from before a price change are counted too; that can be off by a centavo.)
 */
export function buildTap(
  plan: Pick<QuitPlan, 'price'>,
  dayLogs: readonly SmokeLogLike[],
  input: { trigger?: string | null },
  ctx: BuildCtx,
): QuitResult<NewSmokeLog, LogError> {
  const trigger = input.trigger ?? null;
  if (trigger !== null && !isTrigger(trigger)) return { ok: false, error: 'bad_trigger' };
  const date = localDate(ctx.now, ctx.tz);
  const n = tapsSinceTotal(dayLogs, date);
  const log: SmokeLogLike = {
    id: ctx.newId(),
    at: ctx.now,
    date,
    kind: 'tap',
    count: 1,
    costMinor: costOf(n + 1, plan.price) - costOf(n, plan.price),
    trigger,
    deletedAt: null,
  };
  const mutation = insertLog(log, ctx);
  const tally = tallyDay([...dayLogs, log], log.date);
  return {
    ok: true,
    log,
    mutation,
    tally,
    event: smokeEvent('smoke.logged', log.id, log.at, tally, mutation.id),
  };
}

/** "I smoked N on this day": today or a backfilled past day. `dayLogs` = that day's stored logs. */
export function buildDayTotal(
  plan: Pick<QuitPlan, 'price'>,
  dayLogs: readonly SmokeLogLike[],
  input: { date: LocalDate; count: number },
  ctx: BuildCtx,
): QuitResult<NewSmokeLog, LogError> {
  if (!Number.isInteger(input.count) || input.count < 0 || input.count > MAX_DAY_COUNT)
    return { ok: false, error: 'bad_count' };
  // A real calendar day only: 2026-02-31 matches the shape but isn't one.
  if (!isValidLocalDate(input.date)) return { ok: false, error: 'bad_date' };
  if (compareDates(input.date, localDate(ctx.now, ctx.tz)) > 0)
    return { ok: false, error: 'future_date' };
  const log: SmokeLogLike = {
    id: ctx.newId(),
    at: ctx.now,
    date: input.date,
    kind: 'day_total',
    count: input.count,
    costMinor: costOf(input.count, plan.price),
    trigger: null,
    deletedAt: null,
  };
  const mutation = insertLog(log, ctx);
  const tally = tallyDay([...dayLogs, log], input.date);
  return {
    ok: true,
    log,
    mutation,
    tally,
    event: smokeEvent('smoke.total_set', log.id, log.at, tally, mutation.id),
  };
}

/** Undo of a +1, or deleting a log in the day editor. */
export function buildRemoveLog(
  dayLogs: readonly SmokeLogLike[],
  logId: string,
  ctx: BuildCtx,
): QuitResult<SmokeChange, LogError> {
  const target = dayLogs.find((l) => l.id === logId && l.deletedAt === null);
  if (!target) return { ok: false, error: 'not_found' };
  const mutation = quitMutation(ctx, 'smoke_logs', 'delete', logId);
  const tally = tallyDay(
    dayLogs.map((l) => (l.id === logId ? { ...l, deletedAt: ctx.now } : l)),
    target.date,
  );
  return {
    ok: true,
    mutation,
    tally,
    event: smokeEvent('smoke.removed', logId, ctx.now, tally, mutation.id),
  };
}

/** Undo of a delete: a plain insert on the soft-deleted id restores it (conventions §14). */
export function buildRestoreLog(
  dayLogs: readonly SmokeLogLike[],
  logId: string,
  ctx: BuildCtx,
): QuitResult<SmokeChange, LogError> {
  const target = dayLogs.find((l) => l.id === logId && l.deletedAt !== null);
  if (!target) return { ok: false, error: 'not_found' };
  const mutation = quitMutation(ctx, 'smoke_logs', 'insert', logId, {});
  const tally = tallyDay(
    dayLogs.map((l) => (l.id === logId ? { ...l, deletedAt: null } : l)),
    target.date,
  );
  return {
    ok: true,
    mutation,
    tally,
    event: smokeEvent('smoke.logged', logId, ctx.now, tally, mutation.id),
  };
}

export function buildCraving(
  input: { resisted: boolean; trigger?: string | null; delayUsed: boolean },
  ctx: BuildCtx,
): QuitResult<{ craving: CravingLike; mutation: Mutation }, LogError> {
  const trigger = input.trigger ?? null;
  if (trigger !== null && !isTrigger(trigger)) return { ok: false, error: 'bad_trigger' };
  const craving: CravingLike = {
    id: ctx.newId(),
    at: ctx.now,
    date: localDate(ctx.now, ctx.tz),
    resisted: input.resisted,
    trigger,
    delayUsed: input.delayUsed,
    deletedAt: null,
  };
  const mutation = quitMutation(ctx, 'cravings', 'insert', craving.id, {
    at: craving.at,
    recordedTz: ctx.tz,
    date: craving.date,
    resisted: craving.resisted,
    trigger,
    delayUsed: craving.delayUsed,
  });
  return { ok: true, craving, mutation };
}
